import React, { useState,useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Input,
  Select,
  Image,
  useToast,
  Spinner,
  InputGroup,
  InputRightElement,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import car_second_icon from '../Assets/icons/car_second_icon.svg';
import calendar_icon from '../Assets/icons/calender_icon.svg';
import odometer_icon from '../Assets/icons/odometer_icon.svg';
import uploadIcon from '../Assets/icons/uploadDocumentIconBlue.svg';
import cameraIcon from '../Assets/icons/cameraIcon.svg';
import email_icon from '../Assets/icons/email_icon.svg';
import { useCreateVehicle, useCreateVehicleWithEmail, useGetVehicle, useUpdateVehicle } from '../utils/vehicles.api';
import { useProcessVehicleImage, useSendImageMessage, useSendVoiceMessage } from '../utils/conversation.api';

const createInitialFormState = () => ({
  vehicleName: '',
  vin: '',
  make: '',
  year: '',
  license_plate: '',
  model: '',
  type: '',
  odometer: '',
  email: ''
});

const REQUIRED_VEHICLE_FIELDS = [
  { key: 'make', label: 'Make' },
  { key: 'year', label: 'Year' },
];

const VehicleDetailsForm = ({ 
  isOpen, 
  onClose, 
  vehicleToEdit, 
  onEditComplete, 
  storedMedia,
  mode = 'modal',
}) => {
  const fileInputRef = useRef(null); // file input ref
  const cameraInputRef = useRef(null); // camera input ref for license plate
  const toast = useToast();
  const navigate = useNavigate();
  const createVehicleMutation = useCreateVehicle();
  const createVehicleWithEmailMutation = useCreateVehicleWithEmail();
  const updateVehicleMutation = useUpdateVehicle();
  const processVehicleImageMutation = useProcessVehicleImage();
  const sendImageMessageMutation = useSendImageMessage();
  const sendVoiceMessageMutation = useSendVoiceMessage();
  
  // Get vehicle data when editing
  const { data: vehicleData, isLoading: isLoadingVehicle } = useGetVehicle(vehicleToEdit?.id);
  
  const [formDataState, setFormData] = useState(createInitialFormState);
  const formData = formDataState ?? createInitialFormState();

  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [vinError, setVinError] = useState('');
  const [isCameraProcessing, setIsCameraProcessing] = useState(false);
  const modalSize = useBreakpointValue({ base: 'full', md: 'xl' });
  const sectionSpacing = useBreakpointValue({ base: 3, md: 4 });
  const fieldSpacing = useBreakpointValue({ base: 1.5, md: 2 });
  const sectionPaddingX = useBreakpointValue({ base: 4, md: 6 });
  const isMobileView = useBreakpointValue({ base: true, md: false });
  const isPageMode = mode === 'page';
  const areRequiredFieldsFilled = React.useMemo(() => {
    return REQUIRED_VEHICLE_FIELDS.every(({ key }) => {
      const value = formData?.[key];
      return value !== undefined && value !== null && value.toString().trim() !== '';
    });
  }, [formData]);

  const handleFormCompletion = React.useCallback(() => {
    if (isPageMode) {
      navigate('/dashboard');
    } else if (onClose) {
      onClose();
    }
  }, [isPageMode, navigate, onClose]);

  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');

  // Populate form when vehicle data is loaded for editing
  React.useEffect(() => {
    if (vehicleData && vehicleToEdit) {
      setFormData({
        vehicleName: vehicleData.name || '',
        vin: vehicleData.vin || '',
        make: vehicleData.make || '',
        year: vehicleData.year ? vehicleData.year.toString() : '',
        license_plate: vehicleData.license_plate || '',
        model: vehicleData.model || '',
        type: vehicleData.type || '',
        odometer: vehicleData.mileage ? vehicleData.mileage.toString() : '',
        email: ''
      });
    } else if (!vehicleToEdit) {
      // Reset form when not editing
      setFormData({
        vehicleName: '',
        vin: '',
        make: '',
        year: '',
        license_plate: '',
        model: '',
        type: '',
        odometer: '',
        email: ''
      });
    }
  }, [vehicleData, vehicleToEdit]);

  const validateVin = (vin) => {
    // VIN should be exactly 17 characters
    if (vin.length > 0 && vin.length < 17) {
      return 'VIN must be exactly 17 characters long';
    }
    // VIN should contain only alphanumeric characters (no spaces, special characters)
    if (vin.length > 0 && !/^[A-Za-z0-9]+$/.test(vin)) {
      return 'VIN can only contain letters and numbers';
    }
    return '';
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleInputChange = (field, value) => {
    // Handle VIN input with character limit and validation
    if (field === 'vin') {
      // Limit to 17 characters and convert to uppercase
      const vinValue = value.toUpperCase().slice(0, 17);
      const error = validateVin(vinValue);
      setVinError(error);
      
      setFormData(prev => ({
        ...prev,
        [field]: vinValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsCameraProcessing(true);
    
    try {
      const response = await processVehicleImageMutation.mutateAsync(file);
      console.log('Camera OCR Response:', response);
      
      // Auto-populate form fields with API response data
      if (response) {
        const updatedFormData = { ...formData };
        
        // Map API response fields to form fields, picking first element from arrays
        if (response["car_name"] && Array.isArray(response["car_name"])) {
          updatedFormData.vehicleName = response["car_name"][0];
        } else if (response["car_name"]) {
          updatedFormData.vehicleName = response["car_name"];
        }
        
        if (response["vin"] && Array.isArray(response["vin"])) {
          updatedFormData.vin = response["vin"][0];
        } else if (response["vin"]) {
          updatedFormData.vin = response["vin"];
        }
        
        if (response["year"] && Array.isArray(response["year"])) {
          updatedFormData.year = response["year"][0];
        } else if (response["year"]) {
          updatedFormData.year = response["year"];
        }
        
        if (response["make"]) {
          updatedFormData.make = response["make"];
        }
        
        if (response["model"] && Array.isArray(response["model"])) {
          updatedFormData.model = response["model"][0];
        } else if (response["model"]) {
          updatedFormData.model = response["model"];
        }
        
        // Focus on license plate if it's detected
        if (response["license_plate"] && Array.isArray(response["license_plate"])) {
          updatedFormData.license_plate = response["license_plate"][0];
        } else if (response["license_plate"]) {
          updatedFormData.license_plate = response["license_plate"];
        }
        
        setFormData(updatedFormData);
      }
      
      toast({
        title: "Success",
        description: "License plate image processed successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to process camera image:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process license plate image. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCameraProcessing(false);
      // Clear the file input
      if (cameraInputRef.current) {
        cameraInputRef.current.value = "";
      }
    }
  };

  const handleProcessImage = async () => {
    if (!uploadedImage) {
      toast({
        title: "No Image",
        description: "Please upload an image first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await processVehicleImageMutation.mutateAsync(uploadedImage);
      console.log('Vehicle Image Processing Response:', response);
      
      // Auto-populate form fields with API response data
      if (response) {
        const updatedFormData = { ...formData };
        
        // Map API response fields to form fields, picking first element from arrays
        if (response["car_name"] && Array.isArray(response["car_name"])) {
          updatedFormData.vehicleName = response["car_name"][0];
        } else if (response["car_name"]) {
          updatedFormData.vehicleName = response["car_name"];
        }
        
        if (response["vin"] && Array.isArray(response["vin"])) {
          updatedFormData.vin = response["vin"][0];
        } else if (response["vin"]) {
          updatedFormData.vin = response["vin"];
        }
        
        if (response["year"] && Array.isArray(response["year"])) {
          updatedFormData.year = response["year"][0];
        } else if (response["year"]) {
          updatedFormData.year = response["year"];
        }
        
        if (response["make"]) {
          updatedFormData.make = response["make"];
        }
        
        if (response["model"] && Array.isArray(response["model"])) {
          updatedFormData.model = response["model"][0];
        } else if (response["model"]) {
          updatedFormData.model = response["model"];
        }
        
        setFormData(updatedFormData);
      }
      
      toast({
        title: "Success",
        description: "Vehicle image processed successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to process vehicle image:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process vehicle image. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const clearImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetFormState = () => {
    setFormData({
      vehicleName: '',
      vin: '',
      make: '',
      year: '',
      license_plate: '',
      model: '',
      type: '',
      odometer: '',
      email: ''
    });
    setUploadedImage(null);
    setImagePreview(null);
    setVinError('');
    setIsCameraProcessing(false);
  };

  // Convert base64 data back to File object
  const base64ToFile = (base64Data, filename, mimeType) => {
    const byteCharacters = atob(base64Data.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new File([byteArray], filename, { type: mimeType });
  };

  // Send stored media to conversation
  const sendStoredMedia = async (vehicleId, conversationId) => {
    if (!storedMedia) return;

    // Determine media type for message
    const mediaTypes = [];
    if (storedMedia.image) mediaTypes.push('image');
    if (storedMedia.audio) mediaTypes.push('audio');

    // Dispatch event to show loader
    window.dispatchEvent(new CustomEvent('mediaLoadingStart', {
      detail: {
        message: `Sending ${mediaTypes.join(' and ')} to conversation...`
      }
    }));

    try {
      // Send image if available
      if (storedMedia.image) {
        const imageFile = base64ToFile(
          storedMedia.image.data,
          storedMedia.image.name,
          storedMedia.image.type
        );
        
        await sendImageMessageMutation.mutateAsync({
          conversationId: conversationId,
          imageFile: imageFile,
          prompt: `Vehicle image for ${formData.vehicleName || 'new vehicle'}`
        });
        
        console.log('✅ Stored image sent to conversation');
      }

      // Send audio if available
      if (storedMedia.audio) {
        const audioFile = base64ToFile(
          storedMedia.audio.data,
          storedMedia.audio.name,
          storedMedia.audio.type
        );
        
        await sendVoiceMessageMutation.mutateAsync({
          conversationId: conversationId,
          voiceFile: audioFile,
          additionalData: {
            vehicle_id: vehicleId,
            prompt: `Voice message for ${formData.vehicleName || 'new vehicle'}`
          }
        });
        
        console.log('✅ Stored audio sent to conversation');
      }

      // Clean up localStorage after successful send
      localStorage.removeItem('capturedImages');
      localStorage.removeItem('recordedAudio');
      
      // Trigger a refresh of the conversation API by dispatching a custom event
      window.dispatchEvent(new CustomEvent('conversationRefresh', { 
        detail: { conversationId, vehicleId } 
      }));
      
      // Dispatch event to hide loader and show success
      window.dispatchEvent(new CustomEvent('mediaLoadingComplete', {
        detail: {
          success: true,
          message: `Media sent to conversation successfully!`
        }
      }));

    } catch (error) {
      console.error('Failed to send stored media:', error);
      
      // Dispatch event to hide loader and show error
      window.dispatchEvent(new CustomEvent('mediaLoadingComplete', {
        detail: {
          success: false,
          message: 'Vehicle created but failed to send stored media. You can upload it manually later.'
        }
      }));
    }
  };

  const handleContinue = async () => {
    try {
      const missingField = REQUIRED_VEHICLE_FIELDS.find(({ key }) => {
        const value = formData?.[key];
        return !value || value.toString().trim() === '';
      });

      if (missingField) {
        toast({
          title: "Missing Information",
          description: `${missingField.label} is required`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Validate email for non-logged-in users
      if (!isLoggedIn) {
        const emailValidationError = validateEmail(formData.email);
        if (emailValidationError) {
          toast({
            title: "Validation Error",
            description: emailValidationError,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }
      }

      // Validate VIN format if provided (VIN is optional)
      const vinValidationError = validateVin(formData.vin);
      if (vinValidationError) {
        setVinError(vinValidationError);
        toast({
          title: "Validation Error",
          description: vinValidationError,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (!isLoggedIn) {
        const vehiclePayload = {
          make: formData.make,
          model: formData.model,
          year: formData.year ? parseInt(formData.year, 10) : null,
          vin: formData.vin,
          license_plate: formData.license_plate,
          mileage: formData.odometer ? parseInt(formData.odometer, 10) : null,
        };

        Object.keys(vehiclePayload).forEach(key => {
          if (vehiclePayload[key] === '' || vehiclePayload[key] === null || vehiclePayload[key] === undefined) {
            delete vehiclePayload[key];
          }
        });

        const response = await createVehicleWithEmailMutation.mutateAsync({
          email: formData.email.trim(),
          vehicle: vehiclePayload,
        });

        toast({
          title: response?.is_unregistered ? "Complete Sign Up" : "Vehicle Linked",
          description: response?.message || (response?.is_unregistered
            ? "We saved your vehicle. Please sign up to continue."
            : "We matched an existing account. Please log in to continue."),
          status: response?.is_unregistered ? "info" : "success",
          duration: 4000,
          isClosable: true,
        });

        const normalizedEmail = (response?.email || formData.email || '').trim();

        if (response?.is_unregistered) {
          if (normalizedEmail) {
            localStorage.setItem('signupEmail', normalizedEmail);
            localStorage.setItem('fromVehicleForm', 'true');
          }
          localStorage.setItem('tempVehicleFormData', JSON.stringify({
            ...formData,
            email: normalizedEmail,
          }));
        } else if (normalizedEmail) {
          localStorage.setItem('loginEmail', normalizedEmail);
        }

        if (!isPageMode && onClose) {
          onClose();
        }

        setTimeout(() => {
          navigate(response?.is_unregistered ? '/sign-up' : '/sign-in');
        }, 800);

        resetFormState();
        return;
      }

      // Logged-in flow
      const userId = localStorage.getItem('userid');

      if (!userId) {
        toast({
          title: "Error",
          description: "User not authenticated. Please login again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Map existing UI fields to API payload structure
      const payload = {
        make: formData.make,
        model: formData.model,
        year: formData.year ? parseInt(formData.year, 10) : null,
        vin: formData.vin,
        license_plate: formData.license_plate,
        mileage: formData.odometer ? parseInt(formData.odometer, 10) : null,
        user_id: userId,
      };

      // Remove empty/undefined fields (except user_id)
      Object.keys(payload).forEach(key => {
        if (key === 'user_id') return;
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      if (vehicleToEdit) {
        // Update existing vehicle
        await updateVehicleMutation.mutateAsync({
          vehicleId: vehicleToEdit.id,
          updateData: payload
        });
        
        toast({
          title: "Success",
          description: "Vehicle updated successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        if (onEditComplete) {
          onEditComplete();
        }
        handleFormCompletion();
      } else {
        // Create new vehicle
        const vehicleResponse = await createVehicleMutation.mutateAsync(payload);
        
        toast({
          title: "Success",
          description: "Vehicle created successfully!",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // If this is from the stored media flow, trigger auto-selection immediately
        if (storedMedia && vehicleResponse) {
          // Dispatch event to trigger auto-selection of the new vehicle
          window.dispatchEvent(new CustomEvent('vehicleCreated', { 
            detail: { 
              vehicle: vehicleResponse,
              fromStoredMediaFlow: true 
            } 
          }));
        }

        // Close the form immediately after vehicle creation
        handleFormCompletion();

        // If we have stored media, send it in the background (don't block form closing)
        if (storedMedia && vehicleResponse && vehicleResponse.id) {
          const vehicleId = vehicleResponse.id;
          const conversationId = vehicleResponse.conversation_id;
          
          if (conversationId) {
            // Send stored media to the conversation in background
            sendStoredMedia(vehicleId, conversationId).catch(error => {
              console.error('Background media sending failed:', error);
            });
          } else {
            console.warn('Vehicle created but no conversation_id found');
            toast({
              title: "Info",
              description: "Vehicle created but no conversation found. Media will be available for manual upload.",
              status: "info",
              duration: 3000,
              isClosable: true,
            });
          }
        }
      }

      resetFormState();
    } catch (error) {
      console.error('Failed to save vehicle:', error);
      toast({
        title: "Error",
        description: error.message || `Failed to ${vehicleToEdit ? 'update' : 'create'} vehicle. Please try again.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const isLoading = vehicleToEdit
    ? updateVehicleMutation.isLoading
    : (isLoggedIn ? createVehicleMutation.isLoading : createVehicleWithEmailMutation.isLoading);

  const formContent = isLoadingVehicle && vehicleToEdit ? (
    <VStack spacing={4} align="center" py={20}>
      <Spinner size="lg" color="blue.500" />
      <Text color="gray.600">Loading vehicle details...</Text>
    </VStack>
  ) : (
    <>
      <VStack spacing={fieldSpacing} align="center" mb={{ base: 4, md: 6 }} px={sectionPaddingX} pt={{ base: 4, md: 6 }}>
        <Text fontSize="lg" fontWeight="bold" color="gray.900">
          {vehicleToEdit ? 'Edit Vehicle Details' : 'Vehicle Details Form'}
        </Text>
      </VStack>
      <Box px={sectionPaddingX}>
        <Text fontSize="xs" color="gray.800" lineHeight="1.5" fontWeight="600">
          Upload Vehicle Image – include the License Plate, VIN (windshield, dashboard corner, or driver-side door panel), and any supporting documents such as Insurance/Registration cards, Repair Orders, or Vehicle On-Board Computer dashboard screens.
        </Text>
      </Box>
      <VStack spacing={sectionSpacing} align="stretch" px={sectionPaddingX} pt={{ base: 3, md: 4 }}>
        <Box>
          <Input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            ref={fileInputRef}
            display="none"
          />
          {isMobileView ? (
            <>
              {!imagePreview && (
                <HStack spacing={2} pb={3}>
                  <Image src={uploadIcon} alt="Upload" w="4" h="4" />
                  <Input
                    placeholder="Upload Vehicle Image (License Plate, VIN, etc.)"
                    onClick={triggerFileSelect}
                    readOnly
                    borderRadius="md"
                    cursor="pointer"
                    borderColor="gray.500"
                    color="gray.800"
                    _placeholder={{ color: 'gray.600' }}
                    _hover={{ borderColor: 'blue.400', bg: 'gray.100' }}
                    _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px rgba(66,153,225,0.6)' }}
                  />
                </HStack>
              )}
              {imagePreview && (
                <Box
                  mt={1}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={3}
                  position="relative"
                  bg="gray.50"
                  cursor="pointer"
                  onClick={triggerFileSelect}
                >
                  <HStack align="center" spacing={3}>
                    <Image 
                      src={imagePreview} 
                      alt="Uploaded Preview" 
                      w="50px" 
                      h="50px" 
                      objectFit="cover" 
                      borderRadius="md"
                    />
                    <VStack spacing={0} align="flex-start">
                      <Text fontSize="sm" color="gray.700" noOfLines={1}>
                        {uploadedImage?.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Tap to replace or remove
                      </Text>
                    </VStack>
                    <IconButton
                      aria-label="Clear image"
                      icon={<Text fontSize="xs" fontWeight="bold">×</Text>}
                      onClick={clearImage}
                      variant="ghost"
                      size="sm"
                      colorScheme="red"
                      borderRadius="full"
                    />
                  </HStack>
                </Box>
              )}
            </>
          ) : (
            <Box position="relative">
              <Box
                border="2px dashed"
                borderColor="gray.300"
                borderRadius="md"
                p={{ base: 4, md: 6 }}
                textAlign="center"
                cursor="pointer"
                _hover={{
                  borderColor: "blue.400",
                  backgroundColor: "blue.50"
                }}
                transition="all 0.2s"
                position="relative"
                minH={{ base: "120px", md: "200px" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={triggerFileSelect}
              >
                {imagePreview ? (
                  <VStack spacing={2} w="full" h="full">
                    <Box w="full" h="full" borderRadius="md" overflow="hidden" flex="1">
                      <Image 
                        src={imagePreview} 
                        alt="Uploaded Preview" 
                        w="full" 
                        h="full" 
                        maxH="150px" 
                        objectFit="contain" 
                      />
                    </Box>
                    <VStack spacing={1}>
                      <Text fontSize="md" fontWeight="small" color="gray.700">
                        {uploadedImage?.name}
                      </Text>
                    </VStack>
                  </VStack>
                ) : (
                  <VStack spacing={3}>
                    <Image 
                      src={uploadIcon} 
                      alt="Upload" 
                      w="8" 
                      h="8" 
                      opacity={0.7}
                    />
                    <VStack spacing={1}>
                      <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium" color="gray.800">
                        Click to upload or drag
                      </Text>
                      <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                        PNG, JPG, GIF up to 10MB
                      </Text>
                    </VStack>
                  </VStack>
                )}
              </Box>
              {uploadedImage && (
                <IconButton
                  aria-label="Clear image"
                  icon={<Text fontSize="xs" fontWeight="bold">×</Text>}
                  onClick={clearImage}
                  variant="ghost"
                  size="sm"
                  colorScheme="red"
                  borderRadius="full"
                  position="absolute"
                  top={2}
                  right={2}
                  zIndex={3}
                  _hover={{ bg: "red.100" }}
                />
              )}
            </Box>
          )}
        </Box>
        {uploadedImage && (
          <Button
            colorScheme="blue"
            w="full"
            h={{ base: "32px", md: "35px" }}
            borderRadius="8px"
            fontSize={{ base: "xs", md: "sm" }}
            onClick={handleProcessImage}
            isLoading={processVehicleImageMutation.isLoading}
            loadingText="Processing..."
            disabled={processVehicleImageMutation.isLoading}
            leftIcon={<Image src={uploadIcon} alt="Process" w="4" h="4" />}
          >
            {processVehicleImageMutation.isLoading ? <Spinner size="xs" /> : "Process Vehicle Image"}
          </Button>
        )}
      </VStack>

      <VStack spacing={sectionSpacing} align="stretch" px={sectionPaddingX}>
        {/* <VStack spacing={fieldSpacing} paddingTop={2} align="stretch">
          <Text fontSize="sm" fontWeight="medium" color="gray.700" pl="28px">
            Vehicle Name 
          </Text>
          <HStack spacing={3}>
            <Image src={car_second_icon} alt="Car" w="4" h="4" />
            <Input
              placeholder="Enter Vehicle Name "
              value={formData.vehicleName}
              onChange={(e) => handleInputChange('vehicleName', e.target.value)}
              borderRadius="md"
            />
          </HStack>
        </VStack> */}

        <VStack spacing={fieldSpacing} align="stretch">
          <Text fontSize="sm" fontWeight="medium" color="gray.800" pl="28px">
            Vehicle Identification Number (VIN)
            <Text as="span" fontSize="xs" display="block" color="red.500">
              Recommended for full Vehicle Specific Overview
            </Text>
          </Text>

          <HStack spacing={3}>
            <Image src={car_second_icon} alt="Car" w="4" h="4" />
            <InputGroup>
              <Input
                placeholder="Enter the VIN located on the windshield"
                value={formData.vin}
                onChange={(e) => handleInputChange('vin', e.target.value)}
                borderRadius="md"
                borderColor={vinError ? "red.500" : "gray.500"}
                color="gray.800"
                _placeholder={{ color: 'gray.600' }}
                _hover={{ bg: 'gray.100', borderColor: vinError ? "red.500" : "gray.600" }}
                _focus={{
                  borderColor: vinError ? "red.600" : "blue.500",
                  boxShadow: vinError ? "0 0 0 1px red.500" : "0 0 0 1px blue.500"
                }}
                maxLength={17}
                isDisabled={isCameraProcessing}
              />
              <InputRightElement>
                <input
                  id="camera-vin"
                  type="file"
                  accept="image/*"
                  onChange={handleCameraUpload}
                  style={{ display: 'none' }}
                />
                <IconButton
                  as="label"
                  htmlFor="camera-vin"
                  aria-label="Capture VIN"
                  icon={isCameraProcessing ? <Spinner size="xs" /> : <Image src={cameraIcon} alt="Camera" w="4" h="4" />}
                  size="sm"
                  variant="ghost"
                  isLoading={isCameraProcessing}
                  disabled={isCameraProcessing}
                  _hover={{ bg: "gray.100" }}
                />
              </InputRightElement>
            </InputGroup>
          </HStack>
          {vinError && (
            <Text fontSize="xs" color="red.500" pl="28px">
              {vinError}
            </Text>
          )}
          <Text fontSize="xs" color="gray.600" pl="28px">
            {(formData?.vin?.length ?? 0)}/17 characters
          </Text>
        </VStack>

        <HStack spacing={{ base: 3, md: 4 }} flexDir={{ base: "column", md: "row" }}>
          <VStack spacing={fieldSpacing} align="stretch" flex={1} w="full">
            <Text fontSize="sm" fontWeight="medium" color="gray.800" pl="28px">
              License Plate
            </Text>
            <HStack spacing={3}>
              <Image src={car_second_icon} alt="Car" w="4" h="4" />
              <InputGroup>
                <Input
                  placeholder="Enter the license plate number"
                  value={formData.license_plate}
                  onChange={(e) => handleInputChange('license_plate', e.target.value)}
                  borderRadius="md"
                  borderColor="gray.500"
                  color="gray.800"
                  _placeholder={{ color: 'gray.600' }}
                  _hover={{ bg: 'gray.100', borderColor: 'gray.600' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  isDisabled={isCameraProcessing}
                />
                <InputRightElement>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCameraUpload}
                    ref={cameraInputRef}
                    style={{ display: 'none' }}
                  />
                  <IconButton
                    aria-label="Capture license plate"
                    icon={isCameraProcessing ? <Spinner size="xs" /> : <Image src={cameraIcon} alt="Camera" w="4" h="4" />}
                    size="sm"
                    variant="ghost"
                    onClick={() => cameraInputRef.current?.click()}
                    isLoading={isCameraProcessing}
                    disabled={isCameraProcessing}
                    _hover={{ bg: "gray.100" }}
                  />
                </InputRightElement>
              </InputGroup>
            </HStack>
          </VStack>
          <VStack spacing={fieldSpacing} align="stretch" flex={1} w="full">
            <Text fontSize="sm" fontWeight="medium" color="gray.800" pl="28px">
              Model
            </Text>
            <HStack spacing={3}>
              <Image src={car_second_icon} alt="Car" w="4" h="4" />
              <InputGroup>
                <Input
                  placeholder="Enter the vehicle model (e.g., Camry, F-150)"
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  borderRadius="md"
                  borderColor="gray.500"
                  color="gray.800"
                  _placeholder={{ color: 'gray.600' }}
                  _hover={{ bg: 'gray.100', borderColor: 'gray.600' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  isDisabled={isCameraProcessing}
                />
                <InputRightElement>
                  <input
                    id="camera-model"
                    type="file"
                    accept="image/*"
                    onChange={handleCameraUpload}
                    style={{ display: 'none' }}
                  />
                  <IconButton
                    as="label"
                    htmlFor="camera-model"
                    aria-label="Capture model"
                    icon={isCameraProcessing ? <Spinner size="xs" /> : <Image src={cameraIcon} alt="Camera" w="4" h="4" />}
                    size="sm"
                    variant="ghost"
                    isLoading={isCameraProcessing}
                    disabled={isCameraProcessing}
                    _hover={{ bg: "gray.100" }}
                  />
                </InputRightElement>
              </InputGroup>
            </HStack>
          </VStack>
        </HStack>

        <HStack spacing={{ base: 3, md: 4 }} flexDir={{ base: "column", md: "row" }}>
          <VStack spacing={fieldSpacing} align="stretch" flex={1} w="full">
            <Text fontSize="sm" fontWeight="medium" color="gray.800" pl="28px">
              Make *
            </Text>
            <HStack spacing={3}>
              <Image src={car_second_icon} alt="Car" w="4" h="4" />
              <InputGroup>
                <Input
                  placeholder="Enter the vehicle make (e.g., Toyota, Ford)"
                  value={formData.make}
                  onChange={(e) => handleInputChange('make', e.target.value)}
                  borderRadius="md"
                  borderColor="gray.500"
                  color="gray.800"
                  _placeholder={{ color: 'gray.600' }}
                  _hover={{ bg: 'gray.100', borderColor: 'gray.600' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  isDisabled={isCameraProcessing}
                />
                <InputRightElement>
                  <input
                    id="camera-make"
                    type="file"
                    accept="image/*"
                    onChange={handleCameraUpload}
                    style={{ display: 'none' }}
                  />
                  <IconButton
                    as="label"
                    htmlFor="camera-make"
                    aria-label="Capture make"
                    icon={isCameraProcessing ? <Spinner size="xs" /> : <Image src={cameraIcon} alt="Camera" w="4" h="4" />}
                    size="sm"
                    variant="ghost"
                    isLoading={isCameraProcessing}
                    disabled={isCameraProcessing}
                    _hover={{ bg: "gray.100" }}
                  />
                </InputRightElement>
              </InputGroup>
            </HStack>
          </VStack>
          <VStack spacing={fieldSpacing} align="stretch" flex={1} w="full">
            <Text fontSize="sm" fontWeight="medium" color="gray.800" pl="28px">
              Year *
            </Text>
            <HStack spacing={3}>
              <Image src={calendar_icon} alt="Calendar" w="4" h="4" />
              <InputGroup>
                <Input
                  placeholder="Enter the vehicle year (e.g., 2020)"
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value)}
                  borderRadius="md"
                  borderColor="gray.500"
                  color="gray.800"
                  _placeholder={{ color: 'gray.600' }}
                  _hover={{ bg: 'gray.100', borderColor: 'gray.600' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  isDisabled={isCameraProcessing}
                />
                <InputRightElement>
                  <input
                    id="camera-year"
                    type="file"
                    accept="image/*"
                    onChange={handleCameraUpload}
                    style={{ display: 'none' }}
                  />
                  <IconButton
                    as="label"
                    htmlFor="camera-year"
                    aria-label="Capture year"
                    icon={isCameraProcessing ? <Spinner size="xs" /> : <Image src={cameraIcon} alt="Camera" w="4" h="4" />}
                    size="sm"
                    variant="ghost"
                    isLoading={isCameraProcessing}
                    disabled={isCameraProcessing}
                    _hover={{ bg: "gray.100" }}
                  />
                </InputRightElement>
              </InputGroup>
            </HStack>
          </VStack>
        </HStack>

        <HStack spacing={{ base: 3, md: 4 }}>
          <VStack spacing={fieldSpacing} align="stretch" flex={1}>
            <Text fontSize="sm" fontWeight="medium" color="gray.800" pl="28px">
              Odometer Reading
            </Text>
            <HStack spacing={3}>
              <Image src={odometer_icon} alt="Odometer" w="4" h="4" />
              <InputGroup>
                <Input
                  placeholder="Enter the current odometer reading in miles"
                  value={formData.odometer}
                  onChange={(e) => handleInputChange('odometer', e.target.value)}
                  borderRadius="md"
                  borderColor="gray.500"
                  color="gray.800"
                  _placeholder={{ color: 'gray.600' }}
                  _hover={{ bg: 'gray.100', borderColor: 'gray.600' }}
                  _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
                  isDisabled={isCameraProcessing}
                />
                <InputRightElement>
                  <input
                    id="camera-odometer"
                    type="file"
                    accept="image/*"
                    onChange={handleCameraUpload}
                    style={{ display: 'none' }}
                  />
                  <IconButton
                    as="label"
                    htmlFor="camera-odometer"
                    aria-label="Capture odometer"
                    icon={isCameraProcessing ? <Spinner size="xs" /> : <Image src={cameraIcon} alt="Camera" w="4" h="4" />}
                    size="sm"
                    variant="ghost"
                    isLoading={isCameraProcessing}
                    disabled={isCameraProcessing}
                    _hover={{ bg: "gray.100" }}
                  />
                </InputRightElement>
              </InputGroup>
            </HStack>
          </VStack>
        </HStack>

        {!isLoggedIn && (
          <VStack spacing={fieldSpacing} align="stretch">
            <Text fontSize="sm" fontWeight="medium" color="gray.800" pl="28px">
              Email *
            </Text>
            <HStack spacing={3}>
              <Image src={email_icon} alt="Email" w="4" h="4" />
              <Input
                type="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                borderRadius="md"
                borderColor="gray.500"
                color="gray.800"
                _placeholder={{ color: 'gray.600' }}
                _hover={{ bg: 'gray.100', borderColor: 'gray.600' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
              />
            </HStack>
          </VStack>
        )}

        <Box textAlign="right" mt={{ base: 4, md: 5 }} mb={{ base: 4, md: 5 }}>
          <Button
            colorScheme="blue"
            w="120px"
            h="40px"
            borderRadius="8px"
            fontSize="sm"
            onClick={handleContinue}
            isLoading={isLoading}
            loadingText={vehicleToEdit ? "Updating..." : "Creating..."}
            disabled={isLoading || isLoadingVehicle || !areRequiredFieldsFilled}
          >
            {isLoading ? <Spinner size="xs" /> : (vehicleToEdit ? "Update" : "Continue")}
          </Button>
        </Box>
      </VStack>
    </>
  );

  if (isPageMode) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Box
          maxW="600px"
          mx="auto"
          bg="white"
          minH="100vh"
          display="flex"
          flexDirection="column"
          boxShadow="md"
        >
          <Box
            px={4}
            py={3}
            borderBottom="1px solid"
            borderColor="gray.100"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            bg="white"
            position="sticky"
            top={0}
            zIndex={1}
          >
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard')}
            >
              Back
            </Button>
            <Text fontWeight="bold">
              {vehicleToEdit ? 'Edit Vehicle' : 'Add Vehicle'}
            </Text>
          </Box>
          <Box
            flex="1"
            overflowY="auto"
            css={{ WebkitOverflowScrolling: "touch" }}
            sx={{
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.2)",
                borderRadius: "2px",
              },
            }}
            pb={6}
          >
            {formContent}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      isCentered 
      size={modalSize}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
      <ModalContent 
        bg="white" 
        borderRadius={{ base: "0", md: "30px" }} 
        mx={4}
        maxW="600px"
        borderTopRadius={{ base: "0", md: "0" }}
        borderBottomRadius={{ base: "0", md: "30px" }}
        maxH={{ base: "100vh", md: "90vh" }}
        h={{ base: "100vh", md: "auto" }}
        overflow="hidden"
      >
        <ModalBody 
          p={0} 
          overflowY="auto" 
          maxH={{ base: "100vh", md: "calc(90vh - 32px)" }}
          css={{ WebkitOverflowScrolling: "touch" }}
          sx={{
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "2px",
            },
          }}
        >
          {formContent}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default VehicleDetailsForm; 